import * as XLSX from "xlsx";

// Mérito import of MPr-2026-10. The xlsx is the MPr-2026-6 one, so the same
// sheets and ranges are read (see ../mpr_2026_6/_index.js); the differences are
// in where the values go in this form:
// - CAE ("Condições Validação Inov" C53:D55) -> "Atividade(s) económica(s) do
//   projeto" (beneficiaryCae, { cae, "%" });
// - jobs (F26/H26, F27/H27) -> "Criação líquida de emprego" (beneficiaryJobs:
//   totalJob / qualifiedJob / totalEti / qualifiedEti);
// - financing ("Financiamento Inov" K4:K20) -> 17_Financing.js (column "al",
//   same rows);
// - the pre-project year ("Condições Validação Inov" H7) is not imported: it is
//   always 2025 in this notice;
// - the indicators are automatic here and the capacity-increase table belongs to
//   typology 22, which this notice does not have: neither is imported.

const YEAR_KEYS = ["23", "24", "25", "26", "27", "28", "29", "30", "31", "32"];

// "Financiamento Inov" rows (MPr-2026-6 order) are the rows of 17_Financing.js
// (same SGO rubrics and order): only the input rows are imported, the totals
// are formulas in the form.
const FINANCING_INPUT_ROWS = [1, 2, 3, 4, 6, 7, 9, 10, 11, 12, 13, 15];
const FINANCING_LENGTH = 18;

const sheetRows = (sheet, range, header) =>
  sheet
    ? XLSX.utils.sheet_to_json(sheet, {
        range,
        blankrows: true,
        defval: "",
        skipHidden: false,
        header,
      })
    : [];

const cell = (sheet, address) => sheet?.[address]?.v ?? "";

function splitOnce(str, delimiter) {
  if (!str) return "";
  const index = str.indexOf(delimiter);
  if (index === -1) return "";
  return str.slice(index + delimiter.length).trim();
}

function excelSerialToJSDate(xlSerial) {
  const out = new Date(
    -2209075200000 + (xlSerial - (xlSerial < 61 ? 0 : 1)) * 86400000,
  );
  return isNaN(out.getTime()) ? null : out;
}

const isoDate = (serial, length) =>
  excelSerialToJSDate(serial)?.toISOString().slice(0, length) ?? "";

export const meritoImport = (workbook, formData, jsonData, handleChange, setToast) => {
  const meritoSheet = workbook.Sheets["Mérito Inov"];
  if (!meritoSheet) {
    setToast({
      title: "Mérito",
      message: 'Não foi encontrada a folha "Mérito Inov" no ficheiro.',
      type: "error",
    });
    return;
  }

  // Balanço / DR (same rows as 06_BalanceDR.js).
  const balance = sheetRows(workbook.Sheets["Balanço"], "C9:L71", YEAR_KEYS);
  const dr = sheetRows(workbook.Sheets["DR"], "C11:L40", YEAR_KEYS);

  // Financiamento.
  const fileFinancing = sheetRows(
    workbook.Sheets["Financiamento Inov"],
    "K4:K20",
    ["value"],
  );
  const financing = Array.from({ length: FINANCING_LENGTH }, (_, i) => ({
    ...(formData["financing"]?.[i] ?? {}),
  }));
  if (fileFinancing.length) {
    FINANCING_INPUT_ROWS.forEach((row) => {
      financing[row].al = fileFinancing[row]?.value ?? "";
    });
  }

  // Custos (activities by designation, establishments by address).
  let id = 1;
  const projectCosts = sheetRows(
    workbook.Sheets["Quadro de Investimentos Inov"],
    "B5:J79",
    ["designation", "act", "loc", "u1", "u2", "acquisitionDate", "u4", "category", "eligibleCost"],
  )
    .map((cost) => {
      if (!cost.designation || !(Number(cost.eligibleCost) > 0)) return null;
      const activityLabel = splitOnce(cost.act, "-");
      const locationLabel = splitOnce(cost.loc, "-");
      const locations = formData["projectLocation"] ?? [];
      return {
        id: id++,
        designation: cost.designation,
        category:
          (jsonData["CostsCategory"] ?? []).find(
            (cat) => cat.label === String(cost.category ?? "").trim(),
          )?.value ?? "",
        activity: String(
          (formData["projectActivities"] ?? []).find(
            (act) => act.designation === activityLabel,
          )?.id ?? "",
        ),
        institute:
          locations.length === 1
            ? String(locations[0].id)
            : String(
                locations.find((loc) => loc.address === locationLabel)?.id ?? "",
              ),
        acquisitionDate: isoDate(cost.acquisitionDate, 7),
        eligibleCost: cost.eligibleCost,
      };
    })
    .filter(Boolean);

  // Análise Mercado.
  const activityPerMarket = sheetRows(meritoSheet, "L37:R81", [
    "service", "new", "country", "preQuantity", "preValue", "postQuantity", "postValue",
  ])
    .map((act) => {
      if (!act.country) return null;
      return {
        ...act,
        type: "01",
        service: (jsonData["Services"] ?? []).find((s) => s.label === act.service)?.value,
        new: act.new === "Existente" ? "E" : "N",
        country: (jsonData["Countries"] ?? []).find((c) => c.label === act.country)?.value,
      };
    })
    .filter(Boolean);

  const withoutU1 = (row) => {
    if (!row.label) return null;
    const { u1, ...rest } = row;
    return rest;
  };
  const indirectExportation = sheetRows(meritoSheet, "U29:AC41", [
    "nif", "label", "preSells", "preVolInter", "preVolTotal", "u1", "postSells", "postVolInter", "postVolTotal",
  ])
    .map(withoutU1)
    .filter(Boolean);
  // AE NIF, AF Cliente, AG Tx importação (not in the form), AH/AI pre imports
  // and purchases, AJ/AK post imports and purchases, AL Substituição.
  const subImports = sheetRows(meritoSheet, "AE29:AL41", [
    "nif", "label", "u1", "preImports", "preBuys", "postImports", "postBuys", "subsImports",
  ])
    .map(withoutU1)
    .filter(Boolean);

  // Dados do Projeto ("Condições Validação Inov").
  const conditions = workbook.Sheets["Condições Validação Inov"];
  const startDate = isoDate(cell(conditions, "D9"), 10);
  const endDate = isoDate(cell(conditions, "L9"), 10);
  const cruiseYear = cell(conditions, "L7") ? String(cell(conditions, "L7")) : "";

  const beneficiaryCae = sheetRows(conditions, "C53:D55", ["%", "cae"])
    .filter((row) => row.cae)
    .map((row) => ({ cae: String(row.cae).padStart(5, "0"), "%": row["%"] }));

  const jobs = (total, qualified) => ({
    totalJob: total,
    qualifiedJob: qualified,
    totalEti: total,
    qualifiedEti: qualified,
  });
  const pre = jobs(cell(conditions, "F26"), cell(conditions, "F27"));
  const post = jobs(cell(conditions, "H26"), cell(conditions, "H27"));
  const beneficiaryJobs = [
    { rubric: "Pré-Projeto", ...pre },
    { rubric: "Ano Conclusão + 1", ...post },
    { rubric: "Criação Líquida de Emprego" },
    { rubric: "Ano cruzeiro", ...post },
    { rubric: "Criação Líquida de Emprego" },
  ];

  handleChange("balance", balance);
  handleChange("dr", dr);
  if (fileFinancing.length) handleChange("financing", financing);
  handleChange("projectCosts", projectCosts);
  handleChange("activityPerMarket", activityPerMarket);
  handleChange("indirectExportation", indirectExportation);
  handleChange("subImports", subImports);
  if (startDate) handleChange("projectStartDate", startDate);
  if (endDate) handleChange("projectEndDate", endDate);
  if (cruiseYear) handleChange("projectCruiseYear", cruiseYear);
  if (beneficiaryCae.length) handleChange("beneficiaryCae", beneficiaryCae);
  if (conditions) handleChange("beneficiaryJobs", beneficiaryJobs);

  setToast({
    title: "Mérito",
    message: "Dados foram carregados com sucesso!",
    type: "success",
  });
};
